import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentIcon,
  MapPinIcon,
  TagIcon,
  UserIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  TrophyIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  createCheckoutSession,
  getConfirmedDeals,
  getDealById,
  submitRatingOnly,
  updateFightResultOnly,
} from "../../services/api";
import { DealResponseDto, EntityId } from "../../types/types";
import { formatDate } from "../../utils/formatUtils";
import {
  formatFileSize,
  formatSportName,
  formatWeightClassName,
} from "../../utils/utilMethods";
import {
  DATE_INPUT_MAX,
  limitDateInputYear,
  readLimitedDateInputValue,
  resolveDateInputMax,
} from "../../utils/dateInput";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Tooltip,
} from "@heroui/react";
import DealViewContent from "./dealViewContent";
import { useDealViewRenderers } from "./useDealViewRenderers";
import { useNavigate } from "react-router-dom";

type FightResultType =
  | "WIN"
  | "LOSS"
  | "DRAW"
  | "NO_SHOW_FAULT"
  | "NO_SHOW_MEDICAL"
  | "EVENT_CANCELLED"
  | null;

const getFightResult = (deal: DealResponseDto): string | undefined =>
  (deal as any)["__fightResult__"] ?? undefined;

const getSport = (deal: DealResponseDto): string =>
  deal.formData?.details?.sport ?? deal.formData?.sport ?? "—";

const getWeightClass = (deal: DealResponseDto): string =>
  deal.formData?.details?.weightClass ?? deal.formData?.weightClass ?? "—";

const getLocation = (deal: DealResponseDto): string => {
  const loc = deal.formData?.details?.location ?? deal.formData?.location;
  if (!loc) return "—";
  if (typeof loc === "object") {
    return [loc.city, loc.country].filter(Boolean).join(", ");
  }
  return String(loc);
};

const getEventDate = (deal: DealResponseDto): string =>
  deal.formData?.details?.eventDate ?? deal.formData?.eventDate ?? "";

const getMessage = (deal: DealResponseDto): string =>
  deal.formData?.message ?? deal.formData?.details?.message ?? "";


const isManagerAccepted = (deal: DealResponseDto): boolean =>
  [
    "RESULT_UPDATED",
    "RESULT_CONFIRMED",
    "FIGHTER_RATED",
    "MANAGER_RATED",
    "FINISHED",
  ].includes(deal.status ?? "");

const isFighterAccepted = (deal: DealResponseDto): boolean =>
  ["RESULT_CONFIRMED", "FIGHTER_RATED", "MANAGER_RATED", "FINISHED"].includes(
    deal.status ?? "",
  );

const dealStatusOptions = [
  "ACCEPTED",
  "CONFIRMED",
  "WAITING_FOR_RESULT",
  "RESULT_UPDATED",
  "RESULT_CONFIRMED",
  "FIGHTER_RATED",
  "MANAGER_RATED",
  "FINISHED",
];

const pageSizeOptions = [10, 20, 50];

const statusTranslationKey = (status: string): string =>
  `status.${status.toLowerCase()}`;

const formatStatusLabel = (status: string): string =>
  String(status || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const DealAccordion: React.FC = () => {
  const { t } = useTranslation("dealAccordion");
  const { t: viewDealT } = useTranslation("viewDeal");
  const navigate = useNavigate();

  const [expandedCard, setExpandedCard] = useState<EntityId | null>(null);
  const [dealRequests, setDealRequests] = useState<DealResponseDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [fromDateFilter, setFromDateFilter] = useState<string>("");
  const [toDateFilter, setToDateFilter] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);

  const shouldShowPagination = page > 0 || dealRequests.length === size;
  const [downloadingAgreement, setDownloadingAgreement] = useState<
    Record<string, boolean>
  >({});

  const [detailedDeals, setDetailedDeals] = useState<
    Record<string, DealResponseDto>
  >({});
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [isSubmittingRating, setIsSubmittingRating] = useState<
    Record<string, boolean>
  >({});
  const [isUpdatingResult, setIsUpdatingResult] = useState<
    Record<string, boolean>
  >({});
  const [selectedResults, setSelectedResults] = useState<
    Record<string, FightResultType>
  >({});
  const [userId, setUserId] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<Record<string, boolean>>(
    {},
  );
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isRatingModalOpen, setIsRatingModalOpen] = useState<
    Record<string, boolean>
  >({});

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConfirmedDeals(
        statusFilter || undefined,
        fromDateFilter || undefined,
        toDateFilter || undefined,
        page,
        size,
      );
      setDealRequests(data);
      setExpandedCard(null);
    } catch (err) {
      console.error(err);
      setError(t("errorFetch"));
    } finally {
      setLoading(false);
    }
  }, [fromDateFilter, page, size, statusFilter, t, toDateFilter]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  useEffect(() => {
    const loginResponse = JSON.parse(
      localStorage.getItem("loginResponse") || "{}",
    );
    setUserId(loginResponse.id?.toString?.() ?? null);
  }, []);

  const getCurrentUserId = () => {
    const loginResponse = JSON.parse(
      localStorage.getItem("loginResponse") || "{}",
    );
    return loginResponse.id?.toString?.() ?? "";
  };

  const getUserRole = (): string => {
    const userJson = localStorage.getItem("loginResponse");
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.profileType || "";
  };

  const role = getUserRole();

  const getStatusLabel = useCallback(
    (status: string): string => {
      const key = statusTranslationKey(status);
      const translatedStatus = t(key);
      return translatedStatus === key ? formatStatusLabel(status) : translatedStatus;
    },
    [t],
  );

  const fetchAndStoreDeal = useCallback(async (dealId: EntityId) => {
    try {
      const detailedDeal = await getDealById(dealId);
      setDetailedDeals((prev) => ({ ...prev, [dealId]: detailedDeal }));
      setSelectedResults((prev) => ({
        ...prev,
        [dealId]: (getFightResult(detailedDeal) as FightResultType) || null,
      }));
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load deal details",
      );
    }
  }, []);

  const refreshDeal = useCallback(
    async (dealId?: EntityId): Promise<void> => {
      if (!dealId) return;
      await fetchAndStoreDeal(dealId);
    },
    [fetchAndStoreDeal],
  );

  const toggleExpand = async (cardId: EntityId) => {
    if (expandedCard === cardId) {
      setExpandedCard(null);
      return;
    }
    setExpandedCard(cardId);
    setActiveTabs((prev) => ({ ...prev, [cardId]: "general" }));
    if (!detailedDeals[cardId]) {
      await fetchAndStoreDeal(cardId);
    }
  };

  const handleDownload = (fileUrl: string, fileName: string) => {
    fetch(fileUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const resetFilters = () => {
    setStatusFilter("");
    setFromDateFilter("");
    setToDateFilter("");
    setPage(0);
    setSize(10);
  };

  const handleDateFilterInput = (event: React.FormEvent<HTMLInputElement>) => {
    readLimitedDateInputValue(event.currentTarget);
  };

  const navigateToProfile = (profileId?: number | string | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };

  const handleResultUpdate = async (dealId: EntityId) => {
    if (!selectedResults[dealId]) {
      toast.error("Please select result");
      return;
    }
    setIsUpdatingResult((prev) => ({ ...prev, [dealId]: true }));
    try {
      await updateFightResultOnly(dealId, selectedResults[dealId]!);
      toast.success("Result updated!");
      await fetchAndStoreDeal(dealId);
      const myId = getCurrentUserId();
      const updated = detailedDeals[dealId];
      const hasRated = updated?.ratingResponse?.some(
        (r) => r.ratedBy?.id?.toString() === myId,
      );
      if (!hasRated) {
        setRatings((prev) => ({ ...prev, [dealId]: 0 }));
        setComments((prev) => ({ ...prev, [dealId]: "" }));
        setIsRatingModalOpen((prev) => ({ ...prev, [dealId]: true }));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setIsUpdatingResult((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  const handleResultConfirm = async (dealId: EntityId) => {
    setIsUpdatingResult((prev) => ({ ...prev, [dealId]: true }));
    try {
      await updateFightResultOnly(dealId, selectedResults[dealId]!, true);
      toast.success("Result confirmed!");
      await fetchAndStoreDeal(dealId);
      const myId = getCurrentUserId();
      const updated = detailedDeals[dealId];
      const hasRated = updated?.ratingResponse?.some(
        (r) => r.ratedBy?.id?.toString() === myId,
      );
      if (!hasRated) {
        setRatings((prev) => ({ ...prev, [dealId]: 0 }));
        setComments((prev) => ({ ...prev, [dealId]: "" }));
        setIsRatingModalOpen((prev) => ({ ...prev, [dealId]: true }));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setIsUpdatingResult((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  const submitRatingFromModal = async (dealId: EntityId) => {
    const rt = ratings[dealId];
    if (!rt || rt < 1) {
      toast.error("Please select a rating");
      return;
    }
    setIsSubmittingRating((prev) => ({ ...prev, [dealId]: true }));
    try {
      await submitRatingOnly({
        dealId,
        rating: rt,
        feedback: comments[dealId]?.trim() || undefined,
      });
      toast.success("Rating submitted");
      setIsRatingModalOpen((prev) => ({ ...prev, [dealId]: false }));
      await fetchAndStoreDeal(dealId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to submit rating");
    } finally {
      setIsSubmittingRating((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  const fightResultConfig: Record<
    Exclude<FightResultType, null>,
    { label: string; color: string }
  > = {
  WIN: { label: t("win"), color: "green" },
  LOSS: { label: t("loss"), color: "red" },
  DRAW: { label: t("draw"), color: "blue" },
  NO_SHOW_FAULT: { label: t("no_show_fault"), color: "orange" },
  NO_SHOW_MEDICAL: { label: t("no_show_medical"), color: "yellow" },
  EVENT_CANCELLED: { label: t("event_cancelled"), color: "gray" },
  };

  const handleClickOutside = (dealId: EntityId, event: MouseEvent) => {
    if (
      dropdownRefs.current[dealId] &&
      !dropdownRefs.current[dealId]!.contains(event.target as Node)
    ) {
      setIsDropdownOpen((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      Object.keys(dropdownRefs.current).forEach((dealId) =>
        handleClickOutside(dealId, event),
      );
    };
    document.addEventListener("mousedown", handleGlobalClick);
    return () => document.removeEventListener("mousedown", handleGlobalClick);
  }, []);

  const toTitleCase = (str: string): string =>
    str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  const formatKey = useCallback(
    (k: string) =>
      toTitleCase(
        k
          .replace(/([A-Z])/g, " $1")
          .replace(/_/g, " ")
          .trim(),
      ),
    [],
  );

  const formatValue = useCallback((v: any): React.ReactNode => {
    if (v === null || v === undefined) return t("notSpecified");
    if (typeof v === "boolean") return v ? t("yes") : t("no");
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        return toTitleCase(trimmed.slice(1, -1).trim().replace(/_/g, " "));
      }
      const percent = v.match(/percent_of_base_(\d+)/i);
      if (percent) return `${percent[1]}% Of Base`;
      return toTitleCase(v.replace(/_/g, " "));
    }
    if (Array.isArray(v)) {
      if (v.length === 0) return "[]";
      const preview = v
        .slice(0, 3)
        .map((it) =>
          typeof it === "object" ? JSON.stringify(it) : String(it),
        );
      return `[ ${preview.join(", ")}${v.length > 3 ? ", …" : ""} ]`;
    }
    if (typeof v === "object") {
      const entries = Object.entries(v);
      const primitivePreview = entries
        .filter(([, val]) => typeof val !== "object")
        .slice(0, 3)
        .map(([k, val]) => `${k}: ${String(val)}`);
      const nestedCounts = entries
        .filter(([, val]) => typeof val === "object")
        .map(([k, val]) => {
          if (Array.isArray(val)) return `${k}: [${val.length}]`;
          return `${k}: {…}`;
        });
      const preview = [...primitivePreview, ...nestedCounts].slice(0, 3);
      return `${preview.join(", ")}${entries.length > 3 ? ", …" : ""}`;
    }
    return String(v);
  }, [t]);

  const splitFormData = useCallback((obj: any) => {
    if (!obj || typeof obj !== "object") {
      return { summary: {}, details: {} };
    }
    const summary: Record<string, any> = {};
    const details: Record<string, any> = {};
    Object.entries(obj).forEach(([key, value]) => {
      const isPrimitive =
        value === null ||
        ["string", "number", "boolean"].includes(typeof value);
      const isSmallObject =
        typeof value === "object" &&
        !Array.isArray(value) &&
        value !== null &&
        Object.values(value).every(
          (v) =>
            v === null || ["string", "number", "boolean"].includes(typeof v),
        );
      if (isPrimitive || isSmallObject) {
        summary[key] = value;
      } else {
        details[key] = value;
      }
    });
    return { summary, details };
  }, []);

  const renderDynamicObject = useCallback(
    (obj: any): React.ReactNode => {
      if (!obj || typeof obj !== "object") return null;

      return (
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(obj).map(([key, value]) => {
            if (value === null || value === undefined || value === "")
              return null;
            if (Array.isArray(value) && value.length === 0) return null;
            if (
              typeof value === "object" &&
              !Array.isArray(value) &&
              Object.keys(value).length === 0
            )
              return null;

            if (Array.isArray(value)) {
              return (
                <div key={key} className="col-span-2">
                  <div className="text-xs text-gray-500 mb-1">
                    {formatKey(key)}
                  </div>
                  <div className="space-y-2">
                    {value.map((item, i) => (
                      <div key={i} className="border rounded-lg p-3 bg-gray-50">
                        {typeof item === "object"
                          ? renderDynamicObject(item)
                          : formatValue(item)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            if (typeof value === "object") {
              return (
                <div key={key} className="col-span-2">
                  <div className="text-xs text-gray-500 mb-1">
                    {formatKey(key)}
                  </div>
                  <div className="border rounded-lg p-3 bg-gray-50">
                    {renderDynamicObject(value)}
                  </div>
                </div>
              );
            }

            return (
              <div key={key}>
                <div className="text-[10px] text-gray-500">
                  {formatKey(key)}
                </div>
                <div className="font-semibold text-sm text-gray-900 mt-1">
                  {formatValue(value)}
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    [formatKey, formatValue],
  );

  const renderSummary = useCallback(
    (obj: any): React.ReactNode => {
      if (!obj || Object.keys(obj).length === 0)
        return <div className="text-sm text-gray-500">{t("noSummaryData")}</div>;

      const entries = Object.entries(obj).slice(0, 3);

      return (
        <div className="space-y-4">
          {entries.map(([groupKey, groupValue]) => (
            <div key={groupKey} className="border rounded-lg bg-white p-4">
              <div className="text-xs text-gray-500 mb-3">
                {formatKey(groupKey)}
              </div>

              {typeof groupValue === "object" &&
              groupValue !== null &&
              !Array.isArray(groupValue) ? (
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(groupValue).map(([k, v]) => {
                    if (v === null || v === undefined || v === "") return null;
                    if (Array.isArray(v) && v.length === 0) return null;
                    if (
                      typeof v === "object" &&
                      !Array.isArray(v) &&
                      Object.keys(v).length === 0
                    )
                      return null;

                    return (
                      <div key={k}>
                        <div className="text-[10px] text-gray-500">
                          {formatKey(k)}
                        </div>
                        <div className="font-semibold text-sm text-gray-900 mt-1">
                          {formatValue(v)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div className="text-sm">
                    <div className="font-semibold text-sm text-gray-900">
                      {formatValue(groupValue)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    },
    [formatKey, formatValue, t],
  );

  const handlePayComponent = async (
    dealId: EntityId,
    componentPaymentId: number,
  ) => {
    const loginResponse = JSON.parse(
      localStorage.getItem("loginResponse") || "{}",
    );
    const username = loginResponse.username;

    if (!dealId || !username) return;

    try {
      const checkoutResponse = await createCheckoutSession(
        username,
        dealId,
        componentPaymentId,
      );

      const checkoutUrl =
        (checkoutResponse as any).checkoutUrl || (checkoutResponse as any).url;

      if (!checkoutUrl) return;

      const paymentWindow = window.open(checkoutUrl, "_blank");
      if (!paymentWindow) return;

      const pollInterval = setInterval(() => {
        if (paymentWindow.closed) {
          clearInterval(pollInterval);
          refreshDeal(dealId);
        }
      }, 1000);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          "Component payment failed",
      );
    }
  };

  const handleDownloadAgreement = async (
    dealId: EntityId,
    fileUrl?: string,
    fileName?: string,
  ) => {
    if (!fileUrl) return;

    setDownloadingAgreement((prev) => ({ ...prev, [dealId]: true }));

    try {
      const res = await fetch(fileUrl);
      const blob = await res.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "agreement.pdf";
      a.click();

      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download agreement");
    } finally {
      setDownloadingAgreement((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  const expandedDealForRender =
    expandedCard !== null ? detailedDeals[expandedCard] : null;
  const dealViewRenderers = useDealViewRenderers({
    deal: expandedDealForRender,
    formData: expandedDealForRender?.formData ?? {},
    t: viewDealT,
  });

  return (
    <div className="w-full px-2 md:px-4 lg:px-6 bg-gray-50 min-h-screen">
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {t("headerTitle")}
        </h1>
        <p className="text-sm text-gray-600">{t("headerSubtitle")}</p>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <Select
          placeholder={t("selectStatus")}
          selectedKeys={statusFilter ? [statusFilter] : []}
          onSelectionChange={(keys) => {
            setStatusFilter((Array.from(keys)[0] as string) || "");
            setPage(0);
          }}
          className="w-48 min-w-48"
          size="sm"
        >
          {dealStatusOptions.map((status) => (
            <SelectItem key={status}>{getStatusLabel(status)}</SelectItem>
          ))}
        </Select>

        <Input
          type="date"
          label={t("fromDate")}
          placeholder={t("fromDate")}
          value={limitDateInputYear(fromDateFilter)}
          max={resolveDateInputMax(toDateFilter)}
          pattern="\d{4}-\d{2}-\d{2}"
          onInput={handleDateFilterInput}
          onChange={(event) => {
            setFromDateFilter(readLimitedDateInputValue(event.currentTarget));
            setPage(0);
          }}
          className="w-48 min-w-48"
          size="sm"
        />

        <Input
          type="date"
          label={t("toDate")}
          placeholder={t("toDate")}
          value={limitDateInputYear(toDateFilter)}
          min={limitDateInputYear(fromDateFilter) || undefined}
          max={DATE_INPUT_MAX}
          pattern="\d{4}-\d{2}-\d{2}"
          onInput={handleDateFilterInput}
          onChange={(event) => {
            setToDateFilter(readLimitedDateInputValue(event.currentTarget));
            setPage(0);
          }}
          className="w-48 min-w-48"
          size="sm"
        />

        <Tooltip content={t("resetFilters")}>
          <Button
            isIconOnly
            variant="light"
            onClick={resetFilters}
            className="h-10 w-10 text-gray-600 hover:text-gray-800"
            size="sm"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </Button>
        </Tooltip>
      </div>

      {loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">{t("loading")}</p>
        </div>
      )}

      {error && (
        <div className="text-center py-8 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && dealRequests.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">{t("emptyState")}</p>
        </div>
      )}

      {!loading && !error && dealRequests.length > 0 && (
        <div className="space-y-2">
          {dealRequests.map((deal) => {
            const detailedDeal = detailedDeals[deal.id];
            const { summary, details } = splitFormData(
              detailedDeal?.formData || {},
            );
            const isExpanded = expandedCard === deal.id;
            const fightResult = getFightResult(deal);
            const currentUserId = getCurrentUserId();
            const isManager =
              (deal.acceptedBy?.id?.toString() || "") !== currentUserId;
            const displayProfile =
              role === "FIGHTER" ? deal.initiatedBy : deal.acceptedBy;
            const canPayComponents = [
              "ACCEPTED",
              "CONFIRMED",
              "WAITING_FOR_RESULT",
              "RESULT_UPDATED",
              "RESULT_CONFIRMED",
              "FIGHTER_RATED",
              "MANAGER_RATED",
            ].includes(deal.status || "");

            return (
              <div
                key={deal.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => navigateToProfile(displayProfile?.id)}
                      disabled={!displayProfile?.id}
                      className="flex items-center space-x-3 text-left disabled:cursor-default"
                    >
                      <div className="flex-shrink-0">
                        {displayProfile?.profileImage ? (
                            <img
                            src={displayProfile.profileImage}
                            alt={`${displayProfile.firstName} ${displayProfile.lastName}`}
                              className="w-10 h-10 rounded-full object-cover border border-gray-300"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <UserIcon className="w-5 h-5 text-gray-500" />
                            </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 hover:underline">
                          {displayProfile
                            ? `${displayProfile.firstName ?? ""} ${displayProfile.lastName ?? ""}`.trim()
                            : t("unknownUser")}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(displayProfile as any)?.email}
                        </div>
                      </div>
                    </button>

                    <div className="flex items-center space-x-2 flex-wrap justify-end gap-2">
                      {deal.status && (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-xs font-medium">
                          {getStatusLabel(deal.status)}
                        </span>
                      )}
                      {fightResult && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 border rounded-md text-xs font-medium">
                          {fightResult}
                        </span>
                      )}
                      <button
                        onClick={() => toggleExpand(deal.id)}
                        className="p-1 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4 text-gray-600" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>

                  {!isExpanded && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600">
                      <span className="px-2 py-0.5 bg-gray-100 border rounded font-medium">
                        {deal.dealType}
                      </span>
                      <div className="flex items-center gap-1">
                        <TagIcon className="w-3 h-3" />
                        <span>
                          {formatSportName(getSport(deal))} •{" "}
                          {formatWeightClassName(getWeightClass(deal))}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPinIcon className="w-3 h-3" />
                        <span>{getLocation(deal)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        <span>
                          {getEventDate(deal)
                            ? formatDate(getEventDate(deal))
                            : "—"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {detailedDeal ? (
                      <DealViewContent
                        deal={detailedDeal}
                        formData={detailedDeal.formData}
                        activeTab={activeTabs[deal.id] || "general"}
                        setActiveTab={(tab: string) =>
                          setActiveTabs((prev) => ({ ...prev, [deal.id]: tab }))
                        }
                        t={viewDealT}
                        isFighter={role === "FIGHTER"}
                        canPayComponents={canPayComponents}
                        handlePayComponent={(componentPaymentId) =>
                          handlePayComponent(deal.id, componentPaymentId)
                        }
                        currentUserId={getCurrentUserId() || ""}
                        isManager={isManager}
                        hasUserRated={
                          !!detailedDeal.ratingResponse?.some(
                            (r) =>
                              r.ratedBy?.id?.toString() === getCurrentUserId(),
                          )
                        }
                        refreshDeal={async () => {
                          await refreshDeal(deal.id);
                        }}
                        downloadingAgreement={!!downloadingAgreement[deal.id]}
                        handleDownloadAgreement={() =>
                          handleDownloadAgreement(
                            deal.id,
                            detailedDeal?.agreementFile?.fileUrl,
                            detailedDeal?.agreementFile?.fileName,
                          )
                        }
                        renderDynamicObject={dealViewRenderers.renderDynamicObject}
                        renderSummary={dealViewRenderers.renderSummary}
                         tr={dealViewRenderers.tr}
                      />
                    ) : (
                      <div className="p-6 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-2"></div>
                        <p>{t("loadingDetails")}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && shouldShowPagination && (
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-200 pt-3 text-sm text-gray-600 flex-wrap">
          <span>{t("pageLabel", { page: page + 1 })}</span>
          <div className="flex items-center gap-2">
            <Select
              label={t("pageSize")}
              selectedKeys={[String(size)]}
              onSelectionChange={(keys) => {
                const nextSize = Number(Array.from(keys)[0] || 10);
                setSize(nextSize);
                setPage(0);
              }}
              className="w-36 min-w-36"
              size="sm"
            >
              {pageSizeOptions.map((option) => (
                <SelectItem key={String(option)}>{String(option)}</SelectItem>
              ))}
            </Select>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("previous")}
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={dealRequests.length < size}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealAccordion;
